import { useState, useRef, useEffect, forwardRef, useImperativeHandle, JSX } from 'react';
import './style.scss';
import { TranslationService } from '../../../services/translation.service';
import type { IClientSideStorageItem } from '../../../models/client-side-storage-item.model';
import { ClientSideStorageService } from '../../../services/client-side-storage.service';
import type { IAutocompleteItem } from '../../../models/autocomplete-item.model';
import RequiredAsterisk from '../RequiredAsterisk';
import useFetch, { DataSource, Status } from '../../../hooks/fetch.hook';
import { type IErrorResponse, ErrorLevel } from '../../../models/error-response.model';
import { ErrorResponseHelper } from '../../../helpers/error-response.helper';
import { ErrorHandlerHelper } from '../../../helpers/error-handler.helper';
import { AutocompleteSuggestionSource, Panels } from './autocomplete-base.enum';

// === INTERFACES AND ENUMS ===
export interface IAutocomplete {
  clear: () => void;
  addSelectedItemToHistory: (item: IAutocompleteItem) => void;
}

export interface IAutocompleteHistoryProps {
  historyLabel?: string;
  localStorageItem: IClientSideStorageItem | undefined;
  commonSearches?: IAutocompleteItem[];
  mapItemsToHistoryString?: (items: IAutocompleteItem[]) => string;
  mapHistoryStringToItems?: (historyString: string) => IAutocompleteItem[];
}

export interface IAutocompleteProps {
  selectedItem: IAutocompleteItem | undefined;
  searchText?: string;
  id: string;
  label?: string;
  placeholderText?: string;
  required?: boolean;
  className?: string;
  noResultsText?: string;
  showNoResultsText?: boolean;
  disabled?: boolean;
  historyProps?: IAutocompleteHistoryProps;
  isInvalid?: boolean;
  ariaDescribedBy?: string;
  listAriaLabelledBy?: string;
  dataSource?: DataSource;
  inputName?: string;
  positionRelative?: boolean;
  preventSearch?: boolean;
  createResourceUrl: (searchText: string) => string;
  processReceivedData(data: unknown | undefined, q: string): IAutocompleteItem[] | undefined;
  getSuggestionId(componentId: string, item: IAutocompleteItem, index: number): string | undefined;
  getItemId: (item: IAutocompleteItem) => string | undefined;
  getDisplayName(item: IAutocompleteItem | undefined): string | undefined;
  createSuggestionElement(suggestionId: string, item: IAutocompleteItem, isActive: boolean, suggestionSource: AutocompleteSuggestionSource, onClick: () => void): JSX.Element | null;
  onSelect: (item: IAutocompleteItem | undefined) => void;
  getLeadingElementDisplayName?: () => string;
  createLeadingElement?: (suggestionId: string, isActive: boolean, onClick: () => void) => JSX.Element;
  onLeadingElementSelect?: () => Promise<void>;
  onSearchTextChange?: (newSearchText: string) => void;
  onPanelsChange?: (newPanel: Panels) => void;
  onFocus?: (() => void) | undefined;
}

enum UserRequestedState {
  undefined = 'undefined',
  open = 'open',
  closed = 'closed',
}

// === CONSTANTS ===
const CONST_LEADING_ELEMENT_PSEUDO_INDEX = -99;

// === SETTINGS ===
const maxHistoryLength = 6;

// === FUNCTIONS ===
function getHistoryFromLocalStorage(localStorageItem: IClientSideStorageItem, mapHistoryStringToItems: (historyString: string) => IAutocompleteItem[]): IAutocompleteItem[] {
  try {
    return mapHistoryStringToItems(ClientSideStorageService.getLocalStorage(localStorageItem) ?? '[]');
  } catch {
    ClientSideStorageService.clearLocalStorage(localStorageItem);
    return [];
  }
}

function addItemToHistoryInLocalStorage(
  item: IAutocompleteItem,
  localStorageItem: IClientSideStorageItem,
  getItemId: (item: IAutocompleteItem) => string | undefined,
  mapHistoryStringToItems: (historyString: string) => IAutocompleteItem[],
  mapItemsToHistoryString: (items: IAutocompleteItem[]) => string
) {
  try {
    const currentHistory = getHistoryFromLocalStorage(localStorageItem, mapHistoryStringToItems);
    const updatedHistory = [item, ...currentHistory.filter((l) => getItemId(l) !== getItemId(item))].slice(0, maxHistoryLength);
    ClientSideStorageService.setLocalStorage(localStorageItem, mapItemsToHistoryString(updatedHistory));
  } catch {
    ClientSideStorageService.clearLocalStorage(localStorageItem);
  }
}

function createLeadingElementId(componentId: string): string {
  return `autocomplete-result-item-${componentId}-leading-element`;
}

export default forwardRef<IAutocomplete, IAutocompleteProps>(function Autocomplete(
  {
    selectedItem,
    searchText,
    id,
    label,
    placeholderText,
    required = false,
    className = undefined,
    noResultsText,
    showNoResultsText = true,
    disabled = false,
    historyProps = undefined,
    isInvalid,
    ariaDescribedBy,
    listAriaLabelledBy,
    dataSource = DataSource.backend,
    inputName = undefined,
    positionRelative = true,
    preventSearch = false,
    createResourceUrl,
    processReceivedData,
    getSuggestionId,
    getItemId,
    getDisplayName,
    createSuggestionElement,
    onSelect,
    getLeadingElementDisplayName = undefined,
    createLeadingElement = undefined,
    onLeadingElementSelect = undefined,
    onSearchTextChange = undefined,
    onPanelsChange = undefined,
    onFocus = undefined,
  }: IAutocompleteProps,
  ref
) {
  // === STATE ===
  const preventFetch = !(!preventSearch && !selectedItem && searchText !== getLeadingElementDisplayName?.() && searchText?.trim());
  const url = !preventFetch && searchText ? createResourceUrl(searchText.trim()) : undefined;
  const [{ status: getItemsStatus, data: getItemsData, errorResponse: getItemsErrorResponse }] = useFetch<unknown>(url, preventFetch, true, {
    dataSource,
    keepLastStateIfPrevented: false,
    delayInMs: 300,
  });
  const [userRequestedState, setUserRequestedState] = useState(UserRequestedState.undefined);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const [generalError, setGeneralError] = useState<string | undefined>(undefined);

  // === REFS ===
  const autocompleteRef = useRef<HTMLDivElement>(null);
  const focusTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // === VARIABLES ===
  const inputRef = useRef<HTMLInputElement>(null);
  const resultsListRef = useRef<HTMLUListElement>(null);
  const suggestedItems = searchText ? processReceivedData(getItemsData, searchText) : undefined;
  const noResultMessageId = `autocomplete-result-no-results-item-${id}`;
  const autocompleteErrorId = `autocomplete-error-item-${id}`;
  const errorResponse =
    getItemsErrorResponse !== undefined && getItemsStatus === Status.failed
      ? ErrorResponseHelper.enrichApiPlaneraResaErrorResponse(getItemsErrorResponse as IErrorResponse, (x) => TranslationService.get(x))
      : undefined;
  let visiblePanel = Panels.none;

  if (userRequestedState !== UserRequestedState.closed && !selectedItem && searchText && (getItemsStatus === Status.success || suggestedItems?.length) && suggestedItems) {
    // suggestedItems?.length used to avoid flickering when status is loading but suggestedItems is not undefined ie showing previous results
    visiblePanel = suggestedItems.length > 0 ? Panels.suggestions : Panels.noresults;
  } else if (
    userRequestedState === UserRequestedState.open &&
    !selectedItem &&
    searchText?.trim() === '' &&
    getItemsStatus === Status.notperformed &&
    (createLeadingElement || historyProps?.localStorageItem)
  ) {
    visiblePanel = Panels.history;
  } else if (userRequestedState !== UserRequestedState.closed && (getItemsStatus === Status.failed || generalError !== undefined)) {
    visiblePanel = Panels.errormessage;
  }
  let history =
    visiblePanel === Panels.history && historyProps?.localStorageItem && historyProps.mapHistoryStringToItems
      ? getHistoryFromLocalStorage(historyProps.localStorageItem, historyProps.mapHistoryStringToItems)
      : [];
  let historyUsesFallbackToCommonSearches = false;
  if (history.length === 0 && historyProps?.commonSearches) {
    history = historyProps.commonSearches;
    historyUsesFallbackToCommonSearches = true;
  }

  // === EFFECTS ===
  useEffect(() => {
    onPanelsChange?.(visiblePanel);
  }, [visiblePanel, onPanelsChange]);

  // === INTERFACE IMPLEMENTATION ===
  useImperativeHandle(ref, () => ({
    clear() {
      setUserRequestedState(UserRequestedState.undefined);
      setSelectedSuggestionIndex(-1);
    },
    addSelectedItemToHistory(item: IAutocompleteItem) {
      if (historyProps?.localStorageItem && historyProps.mapHistoryStringToItems && historyProps.mapItemsToHistoryString) {
        addItemToHistoryInLocalStorage(item, historyProps.localStorageItem, getItemId, historyProps.mapHistoryStringToItems, historyProps.mapItemsToHistoryString);
      }
    },
  }));

  function scrollSuggestionIndexIntoView(index: number): void {
    let selectedElement;
    if (visiblePanel === Panels.suggestions) {
      const suggestionId = getSuggestionId(id, suggestedItems![index], index);
      if (suggestionId) {
        selectedElement = document.getElementById(suggestionId);
      }
    } else if (visiblePanel === Panels.history) {
      if (index === CONST_LEADING_ELEMENT_PSEUDO_INDEX) {
        selectedElement = document.getElementById(createLeadingElementId(id));
      } else {
        const suggestionId = getSuggestionId(id, history[index], index);
        if (suggestionId) {
          selectedElement = document.getElementById(suggestionId);
        }
      }
    }
    const parentElement = selectedElement?.parentElement;
    const visibleFrom = parentElement ? parentElement.scrollTop : 0;
    const visibleTo = parentElement ? parentElement.scrollTop + parentElement.offsetHeight : 0;
    const isVisibleInView = selectedElement && parentElement && selectedElement.offsetTop > visibleFrom && selectedElement.offsetTop < visibleTo;
    if (!isVisibleInView && parentElement) {
      parentElement.scrollTo({ top: selectedElement?.offsetTop, behavior: 'smooth' });
    }
  }

  // === EVENT HANDLERS ===
  function handleInputChange(event: React.ChangeEvent<HTMLInputElement>) {
    const newSearchText = event.target.value;
    setSelectedSuggestionIndex(-1);
    setGeneralError(undefined);
    setUserRequestedState(UserRequestedState.undefined);
    onSearchTextChange?.(newSearchText);
    if (selectedItem !== undefined && newSearchText !== getDisplayName(selectedItem)) {
      onSelect(undefined);
    }
  }

  function handleSuggestionClick(item: IAutocompleteItem): void {
    onSearchTextChange?.(getDisplayName(item) ?? '');
    setGeneralError(undefined);
    onSelect(item);
    setUserRequestedState(UserRequestedState.closed); // NV-5299
  }

  async function handleLeadingElementClick(): Promise<void> {
    try {
      await onLeadingElementSelect?.();
      if (onSearchTextChange) {
        onSearchTextChange('');
      }
    } catch (e: unknown) {
      if (ErrorHandlerHelper.isErrorReponse(e)) {
        setGeneralError(e.errorMessage);
      }
    } finally {
      setUserRequestedState(UserRequestedState.undefined);
      setSelectedSuggestionIndex(-1);
    }
  }

  function handleSearchInputKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    function handleSpecialCaseLeadingElement(): { preventFurtherProcessing: boolean } {
      if (!(createLeadingElement && visiblePanel === Panels.history)) {
        return { preventFurtherProcessing: false };
      }

      if (event.key === 'ArrowDown') {
        if (selectedSuggestionIndex === -1) {
          setSelectedSuggestionIndex(CONST_LEADING_ELEMENT_PSEUDO_INDEX);
          scrollSuggestionIndexIntoView(CONST_LEADING_ELEMENT_PSEUDO_INDEX);
          return { preventFurtherProcessing: true };
        }
        if (selectedSuggestionIndex === CONST_LEADING_ELEMENT_PSEUDO_INDEX) {
          setSelectedSuggestionIndex(0);
          scrollSuggestionIndexIntoView(0);
          return { preventFurtherProcessing: true };
        }
      } else if (event.key === 'ArrowUp') {
        if (selectedSuggestionIndex === CONST_LEADING_ELEMENT_PSEUDO_INDEX) {
          return { preventFurtherProcessing: true };
        }
        if (selectedSuggestionIndex === 0) {
          setSelectedSuggestionIndex(CONST_LEADING_ELEMENT_PSEUDO_INDEX);
          scrollSuggestionIndexIntoView(CONST_LEADING_ELEMENT_PSEUDO_INDEX);
          return { preventFurtherProcessing: true };
        }
      } else if (event.key === 'Enter' || event.key === 'Tab') {
        if (selectedSuggestionIndex === CONST_LEADING_ELEMENT_PSEUDO_INDEX) {
          handleLeadingElementClick();
          return { preventFurtherProcessing: true };
        }
        if (event.key === 'Enter' && selectedSuggestionIndex === -1) {
          // See NV-4874: If no item is selected when the user presses Enter, select the leading element
          handleLeadingElementClick();
          return { preventFurtherProcessing: true };
        }
      } else if (event.key === 'Home') {
        setSelectedSuggestionIndex(CONST_LEADING_ELEMENT_PSEUDO_INDEX);
        scrollSuggestionIndexIntoView(CONST_LEADING_ELEMENT_PSEUDO_INDEX);
        return { preventFurtherProcessing: true };
      }

      return { preventFurtherProcessing: false };
    }

    const { preventFurtherProcessing } = handleSpecialCaseLeadingElement();

    if (!preventFurtherProcessing) {
      if (event.key === 'ArrowDown') {
        if (visiblePanel === Panels.suggestions && suggestedItems && selectedSuggestionIndex < suggestedItems.length - 1) {
          setSelectedSuggestionIndex(selectedSuggestionIndex + 1);
          scrollSuggestionIndexIntoView(selectedSuggestionIndex + 1);
        } else if (visiblePanel === Panels.history && selectedSuggestionIndex < history.length - 1) {
          setSelectedSuggestionIndex(selectedSuggestionIndex + 1);
          scrollSuggestionIndexIntoView(selectedSuggestionIndex + 1);
        }
      } else if (event.key === 'ArrowUp') {
        if ((visiblePanel === Panels.suggestions || visiblePanel === Panels.history) && selectedSuggestionIndex > 0) {
          setSelectedSuggestionIndex(selectedSuggestionIndex - 1);
          scrollSuggestionIndexIntoView(selectedSuggestionIndex - 1);
        }
      } else if (event.key === 'Enter' || event.key === 'Tab') {
        let item: IAutocompleteItem | undefined;
        let firstSuggestion: IAutocompleteItem | undefined;
        if (visiblePanel === Panels.suggestions && suggestedItems) {
          if (selectedSuggestionIndex >= 0 && selectedSuggestionIndex < suggestedItems.length) {
            item = suggestedItems[selectedSuggestionIndex];
          }
          firstSuggestion = suggestedItems.length > 0 ? suggestedItems[0] : undefined;
        } else if (visiblePanel === Panels.history) {
          if (selectedSuggestionIndex >= 0 && selectedSuggestionIndex < history.length) {
            item = history[selectedSuggestionIndex];
          }
          firstSuggestion = history.length > 0 ? history[0] : undefined;
        }
        if (item) {
          const displayName = getDisplayName(item);
          if (displayName) {
            onSearchTextChange?.(displayName);
          }
          handleSuggestionClick(item);
        } else if (event.key === 'Tab') {
          setUserRequestedState(UserRequestedState.closed);
        } else if (firstSuggestion) {
          // See NV-4874: If no item is selected when the user presses Enter, select the first suggestion
          const displayName = getDisplayName(firstSuggestion);
          if (displayName) {
            onSearchTextChange?.(displayName);
          }
          handleSuggestionClick(firstSuggestion);
        }
      } else if (event.key === 'Escape') {
        setUserRequestedState(UserRequestedState.closed);
        setSelectedSuggestionIndex(-1);
      } else if (event.key === 'Home') {
        if (visiblePanel === Panels.suggestions || visiblePanel === Panels.history) {
          setSelectedSuggestionIndex(0);
          scrollSuggestionIndexIntoView(0);
        }
      } else if (event.key === 'End') {
        if (visiblePanel === Panels.suggestions || visiblePanel === Panels.history) {
          const newIndex = visiblePanel === Panels.suggestions && suggestedItems ? suggestedItems.length - 1 : history.length - 1;
          setSelectedSuggestionIndex(newIndex);
          scrollSuggestionIndexIntoView(newIndex);
        }
      }
    }
  }

  function handleSearchInputFocus(event: React.FocusEvent<HTMLInputElement>) {
    setUserRequestedState(UserRequestedState.open);
    if (event.currentTarget) {
      // Select all text in any field on focus for easy re-entry. Delay sightly to allow focus to "stick" before selecting.
      focusTimer.current = setTimeout(() => {
        inputRef.current?.focus();
        inputRef.current?.setSelectionRange(0, 9999); // For iOS
      }, 50);
    }
    onFocus?.();
  }

  function handleSearchInputBlur(event: React.FocusEvent<HTMLInputElement>) {
    clearTimeout(focusTimer.current);
    if (resultsListRef.current && resultsListRef.current.contains(event.relatedTarget)) {
      return;
    }
    setUserRequestedState(UserRequestedState.closed);
    setSelectedSuggestionIndex(-1);
  }

  // === HELPERS ===
  function calculateAriaOwns(): string | undefined {
    if (!(visiblePanel === Panels.noresults || visiblePanel === Panels.errormessage)) {
      return undefined;
    }

    return `${visiblePanel === Panels.noresults ? noResultMessageId : ''} ${visiblePanel === Panels.errormessage ? autocompleteErrorId : ''}`;
  }

  function calculateActiveDescendant(): string | undefined {
    if (visiblePanel === Panels.none || visiblePanel === Panels.errormessage || selectedSuggestionIndex === -1) {
      return undefined;
    }

    return selectedSuggestionIndex === CONST_LEADING_ELEMENT_PSEUDO_INDEX
      ? createLeadingElementId(id)
      : getSuggestionId(id, visiblePanel === Panels.suggestions && suggestedItems ? suggestedItems[selectedSuggestionIndex] : history[selectedSuggestionIndex], selectedSuggestionIndex);
  }

  return (
    <div ref={autocompleteRef} className={`autocomplete rounded d-flex flex-nowrap flex-grow-1 align-items-center ${className ?? ''} ${positionRelative ? 'position-relative' : ''}`}>
      {label && (
        <label id={`${id}-label`} className="position-absolute ps-2 my-0" htmlFor={id}>
          {label}
          {required && <RequiredAsterisk />}
        </label>
      )}

      <input
        id={id}
        ref={inputRef}
        className={`position-relative w-100 text-truncate form-control ${isInvalid ? 'is-invalid' : ''}`}
        placeholder={placeholderText}
        value={selectedItem !== undefined ? getDisplayName(selectedItem) : searchText}
        aria-controls={visiblePanel === Panels.suggestions || visiblePanel === Panels.history ? `autocomplete-results-${id}` : undefined}
        aria-expanded={visiblePanel === Panels.suggestions || visiblePanel === Panels.history ? 'true' : 'false'}
        aria-owns={calculateAriaOwns()}
        aria-activedescendant={calculateActiveDescendant()}
        aria-invalid={isInvalid}
        aria-describedby={ariaDescribedBy}
        disabled={disabled}
        aria-autocomplete="list"
        autoComplete="off"
        type="text"
        role="combobox"
        spellCheck="false"
        name={inputName}
        onChange={handleInputChange}
        onFocus={handleSearchInputFocus}
        onBlur={handleSearchInputBlur}
        onKeyDown={handleSearchInputKeyDown}
      />

      {(visiblePanel === Panels.suggestions || visiblePanel === Panels.history) && (
        <ul
          id={`autocomplete-results-${id}`}
          aria-labelledby={label ? `${id}-label` : listAriaLabelledBy}
          role="listbox"
          className="autocomplete-results w-auto position-absolute shadow-sm list-unstyled overflow-auto"
          ref={resultsListRef}
          // Nessesary for event.relatedTarget to work in onBlur
          tabIndex={0}
        >
          {visiblePanel === Panels.history && (createLeadingElement || history.length > 0) && (
            <>
              {createLeadingElement && createLeadingElement(createLeadingElementId(id), selectedSuggestionIndex === CONST_LEADING_ELEMENT_PSEUDO_INDEX, handleLeadingElementClick)}
              {history.length > 0 && (
                <>
                  <li className="d-flex align-items-center p-2 m-0 w-100 border-x border-top" role="presentation">
                    <span className="fw-bold">
                      {!historyUsesFallbackToCommonSearches
                        ? historyProps?.historyLabel || TranslationService.get('common.autocomplete.suggestionlabel.default')
                        : TranslationService.get('common.autocomplete.suggestionlabel.commonsearches')}
                      :
                    </span>
                  </li>
                  {history.map((item, i) => {
                    const suggestionId = getSuggestionId(id, item, i);
                    if (suggestionId) {
                      return createSuggestionElement(
                        suggestionId,
                        item,
                        i === selectedSuggestionIndex,
                        !historyUsesFallbackToCommonSearches ? AutocompleteSuggestionSource.history : AutocompleteSuggestionSource.commonsearches,
                        () => handleSuggestionClick(item)
                      );
                    }
                  })}
                </>
              )}
            </>
          )}
          {visiblePanel === Panels.suggestions &&
            suggestedItems &&
            suggestedItems.map((item, i) => {
              const suggestionId = getSuggestionId(id, item, i);
              if (suggestionId) {
                return createSuggestionElement(suggestionId, item, i === selectedSuggestionIndex, AutocompleteSuggestionSource.search, () => handleSuggestionClick(item));
              }
              return null;
            })}
        </ul>
      )}

      <div role="alert">
        {showNoResultsText && visiblePanel === Panels.noresults && (
          <div className="autocomplete-results w-auto position-absolute shadow list-unstyled overflow-auto">
            <span id={noResultMessageId} className="d-block py-2 px-2 m-0 border font-italic">
              {noResultsText || TranslationService.get('common.autocomplete.noresults.default')}
            </span>
          </div>
        )}

        {visiblePanel === Panels.errormessage && (
          <div className="autocomplete-results w-auto position-absolute shadow list-unstyled overflow-auto">
            <span id={autocompleteErrorId} className="d-block py-2 px-2 m-0 border font-italic text-danger">
              {generalError !== undefined && <span>{generalError}</span>}
              {getItemsStatus === Status.failed && errorResponse?.errorLevel !== ErrorLevel.error && <span>{TranslationService.get('common.autocomplete.error.default')}</span>}
              {getItemsStatus === Status.failed && errorResponse?.errorLevel === ErrorLevel.error && <span>{errorResponse.errorMessage}</span>}
            </span>
          </div>
        )}
      </div>
    </div>
  );
});
